import React, { useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  signOut,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import { useAuth } from "../../context/AuthProvider";

const DELETE_CONFIRMATION = "DELETE";

function getErrorMessage(error) {
  const code = error?.code || "";
  if (
    code.includes("wrong-password") ||
    code.includes("invalid-credential") ||
    code.includes("invalid-login-credentials")
  ) {
    return "That password is incorrect.";
  }
  if (code.includes("too-many-requests")) {
    return "Too many attempts. Please wait a moment and try again.";
  }
  return error?.message
    ?.replace(/^Firebase: /, "")
    ?.replace(/\s*\(functions\/[^)]+\)\.?$/, "") ||
    "We could not delete your account. Please try again.";
}

const DeleteAccountPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDelete =
    password.length > 0 &&
    confirmation === DELETE_CONFIRMATION &&
    !isDeleting;

  async function handleDelete(event) {
    event.preventDefault();
    if (!user?.email || !canDelete) return;

    setError(null);
    setIsDeleting(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      await user.getIdToken(true);

      const deleteAccount = httpsCallable(getFunctions(), "deleteAccount");
      await deleteAccount({confirmation: DELETE_CONFIRMATION});
      await signOut(auth);
      navigate("/signin?accountDeleted=1", {replace: true});
    } catch (deleteError) {
      setError(getErrorMessage(deleteError));
      setIsDeleting(false);
    }
  }

  return (
    <main className="mx-auto mt-6 w-full max-w-2xl pb-10">
      <h2 className="mb-3">Delete account</h2>
      <Alert variant="danger">
        <Alert.Heading>This is permanent</Alert.Heading>
        <p className="mb-2">
          Deleting your account is dangerous and cannot be undone. You will lose access to everything associated with this account,
          including:
        </p>
        <ul className="mb-2">
          <li>Your account profile and its saved subdata</li>
          <li>Your Apple Health connection record</li>
          <li>Your activity and streak history</li>
          <li>Your My Challenge Canada login</li>
        </ul>
      </Alert>

      <Form
        className="rounded-lg border border-red-200 bg-white p-4 shadow-sm"
        onSubmit={handleDelete}
      >
        <Form.Group className="mb-3" controlId="delete-account-password">
          <Form.Label>Confirm your password</Form.Label>
          <Form.Control
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={isDeleting}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="delete-account-confirmation">
          <Form.Label>
            Type <strong>{DELETE_CONFIRMATION}</strong> to continue
          </Form.Label>
          <Form.Control
            type="text"
            autoComplete="off"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            disabled={isDeleting}
            required
          />
        </Form.Group>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline-secondary"
            type="button"
            onClick={() => navigate("/account")}
            disabled={isDeleting}
          >
            Keep my account
          </Button>
          <Button variant="danger" type="submit" disabled={!canDelete}>
            {isDeleting && <Spinner className="mr-2" size="sm" />}
            Delete my account permanently
          </Button>
        </div>
      </Form>
    </main>
  );
};

export default DeleteAccountPage;
